import { useCallback, useRef, useState } from 'react';
import { TodoTree, TodayList } from '../types/todo';
import { HISTORY } from '../utils/constants';

/**
 * One undoable point in time: the task tree plus the day plan that went with
 * it. They are captured together so undoing an archive also brings the task
 * back into the day's list, rather than leaving the two out of sync.
 */
export interface HistorySnapshot {
  tree: TodoTree;
  today: TodayList;
}

export interface TreeHistory {
  /** Record the state that existed *before* a mutation. Clears the redo stack. */
  push: (prev: HistorySnapshot) => void;
  /** Pop the most recent snapshot, pushing `current` onto the redo stack. Null when empty. */
  undo: (current: HistorySnapshot) => HistorySnapshot | null;
  /** Re-apply the most recently undone snapshot, pushing `current` onto the undo stack. Null when empty. */
  redo: (current: HistorySnapshot) => HistorySnapshot | null;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Undo/redo history for the todo tree and its day plan.
 *
 * Snapshots are references to immutable objects — every mutation in
 * TodoContext replaces state via spreads, so storing references is cheap
 * (structural sharing) and safe (snapshots can never be mutated in place).
 *
 * Stacks live in refs so pushing history never causes a render by itself;
 * only the {canUndo, canRedo} flags (which drive toolbar button states) are
 * React state.
 */
export function useTreeHistory(): TreeHistory {
  const undoStackRef = useRef<HistorySnapshot[]>([]);
  const redoStackRef = useRef<HistorySnapshot[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncFlags = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const push = useCallback((prev: HistorySnapshot) => {
    undoStackRef.current.push(prev);
    if (undoStackRef.current.length > HISTORY.MAX_ENTRIES) {
      undoStackRef.current.shift();
    }
    // A fresh edit invalidates any previously undone states.
    redoStackRef.current = [];
    syncFlags();
  }, [syncFlags]);

  const undo = useCallback((current: HistorySnapshot): HistorySnapshot | null => {
    const snapshot = undoStackRef.current.pop();
    if (!snapshot) {
      return null;
    }
    redoStackRef.current.push(current);
    syncFlags();
    return snapshot;
  }, [syncFlags]);

  const redo = useCallback((current: HistorySnapshot): HistorySnapshot | null => {
    const snapshot = redoStackRef.current.pop();
    if (!snapshot) {
      return null;
    }
    undoStackRef.current.push(current);
    syncFlags();
    return snapshot;
  }, [syncFlags]);

  return { push, undo, redo, canUndo, canRedo };
}
